<template>
    <div>
        <q-input
            v-model="filter"
            placeholder="Suchen..."
            class="q-mb-md"
            debounce="300"
            dense
            outlined
            id="user-filter"
        >
            <template v-slot:append>
                <q-icon name="search" />
            </template>
        </q-input>

        <div
            v-if="selected.length > 0"
            class="q-mb-md q-pa-md bg-grey-2 rounded-borders flex justify-between items-center animate-fade"
        >
            <div class="text-subtitle2 text-grey-8">
                {{ selectedLabel }}
            </div>
            <div class="q-gutter-sm flex items-center">
                <q-btn-dropdown
                    color="primary"
                    icon="settings"
                    :label="bulkActionsLabel || 'Bulk-Aktionen'"
                    outlined
                    dense
                    id="bulk-actions-dropdown"
                >
                    <q-list>
                        <q-item
                            clickable
                            v-close-popup
                            @click="executeBulkAction('enable')"
                        >
                            <q-item-section avatar>
                                <q-icon name="check" color="positive" />
                            </q-item-section>
                            <q-item-section>
                                {{
                                    bulkEnableLabel ||
                                    "Für diesen Kalender aktivieren"
                                }}
                            </q-item-section>
                        </q-item>

                        <q-item
                            clickable
                            v-close-popup
                            @click="executeBulkAction('disable')"
                        >
                            <q-item-section avatar>
                                <q-icon name="close" color="negative" />
                            </q-item-section>
                            <q-item-section>
                                {{
                                    bulkDisableLabel ||
                                    "Für diesen Kalender deaktivieren"
                                }}
                            </q-item-section>
                        </q-item>

                        <q-separator />

                        <q-item
                            clickable
                            v-close-popup
                            class="text-negative"
                            @click="confirmBulkDelete"
                        >
                            <q-item-section avatar>
                                <q-icon name="delete" color="negative" />
                            </q-item-section>
                            <q-item-section>
                                {{ bulkDeleteLabel || "Ausgewählte löschen" }}
                            </q-item-section>
                        </q-item>
                    </q-list>
                </q-btn-dropdown>
                <q-btn
                    flat
                    dense
                    color="grey-7"
                    :label="clearSelectionLabel || 'Auswahl aufheben'"
                    @click="clearSelection"
                />
            </div>
        </div>

        <q-table
            class="sticky-container-table"
            :rows="internalRows"
            :columns="columns"
            :filter="filter"
            flat
            bordered
            row-key="id"
            v-model:pagination="pagination"
            :loading="loading"
            @request="onRequest"
            binary-state-sort
            :row-props="getRowProps"
            :selection="multiSelect ? 'multiple' : 'none'"
            v-model:selected="selected"
        >
            <template v-slot:body-cell-tags="props">
                <q-td :props="props">
                    <q-chip
                        v-for="tag in props.row.tags"
                        :key="tag.label"
                        :style="{ backgroundColor: tag.color, color: '#fff' }"
                        size="sm"
                    >
                        {{ tag.label }}
                    </q-chip>
                </q-td>
            </template>

            <template v-slot:body-cell-email="props">
                <q-td :props="props">
                    <span :class="{ 'text-negative': props.row.isBouncer }">
                        {{ props.value }}
                        <q-icon
                            v-if="props.row.isBouncer"
                            name="warning"
                            class="q-ml-xs"
                            :title="emailErrorTitle"
                        />
                    </span>
                </q-td>
            </template>

            <template v-slot:body-cell-login="props">
                <q-td :props="props" class="text-center">
                    <q-icon
                        v-if="props.row.hasPassword"
                        name="check"
                        class="text-positive"
                    />
                    <q-icon v-else name="close" class="text-negative" />
                </q-td>
            </template>

            <template v-slot:body-cell-status="props">
                <q-td :props="props" class="text-center">
                    <q-icon
                        v-if="props.row.active"
                        name="check"
                        class="text-positive"
                    />
                    <q-icon v-else name="close" class="text-negative" />
                </q-td>
            </template>

            <template v-slot:body-cell-actions="props">
                <q-td :props="props" class="text-center">
                    <q-btn
                        color="primary"
                        class="iconButton"
                        :href="props.row.editUrl"
                        icon="edit"
                        size="sm"
                    />
                </q-td>
            </template>
        </q-table>
        <p class="lt-md text-grey">
            {{ mobileScrollHint }}
        </p>
    </div>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import axios from "axios";
import { useQuasar } from "quasar";

export default {
    props: {
        bulkActionsLabel: String,
        rows: Array, // we can keep it for backwards compatibility or initial state, but we'll manage internal state
        columns: Array,
        emailErrorTitle: String,
        deletedTitle: String,
        mobileScrollHint: String,
        multiSelect: {
            type: Boolean,
            default: false,
        },
        bulkDeleteLabel: String,
        bulkEnableLabel: String,
        bulkDisableLabel: String,
        bulkSelectedSingleLabel: String,
        bulkSelectedMultipleLabel: String,
        clearSelectionLabel: String,
    },
    setup(props) {
        const $q = useQuasar();
        const filter = ref("");
        const internalRows = ref([]);
        const loading = ref(false);
        const selected = ref([]);
        const pagination = ref({
            sortBy: "lastName",
            descending: false,
            page: 1,
            rowsPerPage: 10,
            rowsNumber: 10,
        });

        const selectedLabel = computed(() => {
            const count = selected.value.length;
            if (count === 1) {
                return props.bulkSelectedSingleLabel || "1 ausgewählt";
            }
            const template =
                props.bulkSelectedMultipleLabel || ":count ausgewählt";
            return template.replace(":count", count.toString());
        });

        const clearSelection = () => {
            selected.value = [];
        };

        const getRowProps = (row) => {
            if (row.isMarkedToBeDeleted) {
                return {
                    class: "isMarkedToBeDeleted",
                    title: props.deletedTitle,
                };
            }
            return {};
        };

        const executeBulkAction = (action) => {
            if (selected.value.length === 0) return;

            loading.value = true;
            const publisherIds = selected.value.map((row) => row.id);

            axios
                .post("/admin/api/nutzerverwaltung/bulk", {
                    action: action,
                    publisherIds: publisherIds,
                })
                .then((response) => {
                    $q.notify({
                        color: response.data.success ? "positive" : "negative",
                        message:
                            response.data.message ||
                            "Aktion erfolgreich ausgeführt.",
                        icon: response.data.success
                            ? "check"
                            : "report_problem",
                    });
                    selected.value = [];
                    onRequest({
                        pagination: pagination.value,
                        filter: filter.value,
                    });
                })
                .catch((error) => {
                    loading.value = false;
                    $q.notify({
                        color: "negative",
                        message:
                            error.response?.data?.message ||
                            "Fehler beim Ausführen der Aktion.",
                        icon: "report_problem",
                    });
                });
        };

        const confirmBulkDelete = () => {
            const msg = `Möchtest du wirklich ${selected.value.length} Verkündiger löschen / vom Kalender entfernen?`;
            if ($q.dialog) {
                $q.dialog({
                    title: props.bulkDeleteLabel || "Ausgewählte löschen",
                    message: msg,
                    cancel: true,
                    persistent: true,
                }).onOk(() => {
                    executeBulkAction("delete");
                });
            } else {
                if (confirm(msg)) {
                    executeBulkAction("delete");
                }
            }
        };

        const onRequest = (requestProps) => {
            const { page, rowsPerPage, sortBy, descending } =
                requestProps.pagination;
            const filterValue = requestProps.filter;

            loading.value = true;
            selected.value = []; // Clear selected items on table pagination/filtering

            axios
                .get("/admin/api/nutzerverwaltung", {
                    params: {
                        page: page,
                        rowsPerPage: rowsPerPage,
                        sortBy: sortBy,
                        descending: descending,
                        filter: filterValue,
                    },
                })
                .then((response) => {
                    internalRows.value = response.data.rows;
                    pagination.value.rowsNumber = response.data.rowsNumber;

                    pagination.value.page = page;
                    pagination.value.rowsPerPage = rowsPerPage;
                    pagination.value.sortBy = sortBy;
                    pagination.value.descending = descending;

                    loading.value = false;
                })
                .catch((error) => {
                    loading.value = false;
                    $q.notify({
                        color: "negative",
                        message: "Fehler beim Laden der Verkündiger",
                        icon: "report_problem",
                    });
                });
        };

        onMounted(() => {
            onRequest({
                pagination: pagination.value,
                filter: filter.value,
            });
        });

        return {
            filter,
            internalRows,
            pagination,
            loading,
            selected,
            selectedLabel,
            clearSelection,
            executeBulkAction,
            confirmBulkDelete,
            getRowProps,
            onRequest,
        };
    },
};
</script>

<style>
/* Using non-scoped style to ensure it applies to the tr rendered by q-table if scoped doesn't penetrate deep enough, though row-props class applies directly to tr so scoped might work if q-table uses slots correctly. But q-table rows are generated inside its template. Scoped styles in Vue apply to elements in the current component. q-table's internal tr is inside q-table component. However, passed content via slots is in parent scope. But row-props class is applied by q-table logic. Safest is non-scoped or deep selector. */
.isMarkedToBeDeleted {
    text-decoration: line-through;
    opacity: 0.6;
}
</style>
